import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { MetodoPagamento } from "../../core/enum/MetodoPagamento";
import { TipoAnexoPedido } from "../enum/TipoAnexoPedido";
import { TipoPedido } from "../enum/TipoPedido";
import { useFetchPedidos } from "../hooks/useFetchPedidos";
import { usePedidos } from "../hooks/usePedidos";
import { IItemProduto } from "../interface/IItemProduto";
import { IPedido } from "../interface/IPedido";
import { IProdutoPedido } from "../interface/IProdutoPedido";
import { IConfiguracaoPedidos } from "../interface/IConfiguracaoPedidos";
import { useConfiguracoesPedidoContext } from "./ConfiguracoesPedidoContext";
import { TipoProduto } from "../../produtos/enum/TipoProduto";
import { StatusPedido, StatusPedidoDescricao } from "../enum/StatusPedido";
import { IEndereco } from "../../core/interfaces/IEndereco";
import { IRegiao } from "../../core/interfaces/IRegiao";
import moment from "moment";
import { format } from "path";
import { TipoItemProduto } from "../../produtos/enum/TipoItemProduto";
import { EmitirNotaFiscalModal, TipoPessoa } from "../pages/Operacao/containers/EmitirNotaFiscalModal";
import { useHotkeys } from "react-hotkeys-hook";
import { isModoTecladoAtivo } from "../../core/hooks/useModoTeclado";
import { IDadosEmitirNotaFiscal } from "../interface/IDadosEmitirNotaFiscal";
import { validarCpf } from "../../core/utils/validarCpf";
import { validarCnpj } from "../../core/utils/validarCnpj";
import { IFluxoContexto } from "../interface/IFluxoContexto";
import { IConfiguracaoComanda } from "../interface/IConfiguracaoComanda";
import { TipoGatilhoComanda } from "../enum/TipoGatilhoComanda";
import { IProduto } from "../../produtos/interfaces/IProduto";
import { TipoMarketplace } from "../../marketplace/enum/TipoMarketplace";
import { IPagamentoPedido } from "../interface/IPagamentoPedido";
import { useEntregas } from "../hooks/useEntregas";
import { TipoCobrancaEntrega } from "../enum/TipoCobrancaEntrega";
import { useAutenticacaoContext } from "../../core/contexts/AutenticacaoContext";
import { IComplementoItensPedido } from "../interface/IComplementoItensPedido";
import { useLocation, useNavigate, matchPath, useSearchParams } from "react-router-dom";
import { ImprimirModal } from "../components/ImprimirModal";
import { useProdutos } from "../../produtos/hooks/useProdutos";
import { DividirPagamento } from "../pages/Operacao/containers/DividirPagamento";
import { DividirPedido } from "../pages/Operacao/containers/DividirPedido";
import { useWebSocketPedidosContext } from "./WebSocketPedidosContext";
import { ICliente } from "../../clientes/interface/ICliente";
import { ICupom } from "../interface/ICupom";
import { useCuponsPedido } from "../hooks/useCuponsPedido";
import { filtrarCuponsProdutosRemovidos } from "../utils/verificarCupomAssociadoProduto";

interface IOperacaoEdicaoContextProps {
    configuracao: IConfiguracaoPedidos | null | undefined;
    alterarPagamentos: (pagamentos?: IPagamentoPedido[] | undefined) => void;
    adicionarProduto: (produto: IProdutoPedido) => Promise<IProdutoPedido | undefined>;
    adicionarProdutosCombo: (produtos: IProdutoPedido[]) => Promise<void>;
    aumentarQuantidade: (produtoUuid: string) => Promise<void>;
    diminuirQuantidade: (produtoUuid: string) => Promise<void>;
    editarProduto: (produto: IProdutoPedido) => Promise<void>
    removerProduto: (produtoUuid: string) => Promise<void>;
    fecharPedido: (dados?: IDadosEmitirNotaFiscal, pagamentos?: IPagamentoPedido[]) => Promise<void>
    loading: boolean;
    loadingAnexo: boolean;
    pedido?: IPedido;
    /** Recarrega o pedido do servidor (ex.: depois de confirmar uma rodada do QR Code). */
    recarregarPedido: () => Promise<void>;
    cancelarPedido: () => void;
    selecionarProduto: (produto: IProdutoPedido) => void;
    produtosSelecionados: IProdutoPedido[];
    fluxoContexto?: IFluxoContexto;
    numeroTotalItens: number;
    gerarAnexosBotaoImprimirComandas: () => Promise<void>
    gerarAnexosModal: (uuid: string) => Promise<void>
    vincularEndereco: (props?: IEndereco) => Promise<void>
    loadingVincularEndereco: boolean;
    alterarTipo: (tipo: TipoPedido) => void;
    alterarTaxaServico: (percentual?: number) => Promise<void>;
    emitirNotaFiscal: (dados: IDadosEmitirNotaFiscal) => Promise<void>
    abrirModalImpressao: () => void;
    drawerPagamentoAberto: boolean;
    abrirDrawerPagamento: () => void;
    fecharDrawerPagamento: () => void;
    drawerDividirAberto: boolean;
    abrirDrawerDividir: () => void;
    fecharDrawerDividir: () => void;
    dividirPedido: (produtos: string[]) => Promise<IPedido>;
    switchImprimirAutomatico: boolean;
    handleSwitchImprimirAutomatico: () => void;
    configuracaoNFCe?: IConfiguracaoComanda;
    switchNotaFiscal: boolean;
    handleSwitchNotaFiscal: () => void;
    alterarValorFrete: (valor: number) => Promise<void>;
    vincularClientePedido: (cliente: ICliente) => Promise<void>;
    desvincularClientePedido: () => Promise<void>;
    loadingVincularCliente: boolean;
    cupons: ICupom[];
    aplicarCupom: (cupom: ICupom) => Promise<void>;
    removerCupom: (codigo: string, tipo: string) => Promise<void>;
    drawerCuponsAberto: boolean;
    abrirDrawerCupons: () => void;
    fecharDrawerCupons: () => void;
}

export const useOperacaoEdicaoContext = () => {
    return useContext(OperacaoEdicaoContext)
}
export const OperacaoEdicaoContext = createContext({} as IOperacaoEdicaoContextProps)

export type TipoDivisaoPagamento = "Quantidade de pessoas" | "Produtos selecionados" | "Valores selecionados";

export const OperacaoEdicaoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const {
        loading,
        loadingAnexo,
        atualizarStatus,
        adicionarProduto: adicionarProdutoPedido,
        removerProduto: removerProdutoPedido,
        gerarAnexo: gerarAnexoPedido,
        atualizarPagamentos: atualizarPagamentosPedido,
        alterarEndereco: atualizarEndereco,
        emitirNotaFiscal: emitirNotaFiscalConsumidor,
        alterarTipo: alterarTipoPedido,
        alterarTaxaServico: alterarTaxaServicoPedido,
        dividirPedido: dividirPedidoHook,
        buscarPedido,
        vincularCliente: vincularClienteHook,
        desvincularCliente: desvincularClienteHook
    } = usePedidos();

    const { tratarProduto } = useProdutos();

    const snackbar = useSnackbar();
    const [pedido, setPedido] = useState<IPedido | undefined>(undefined);
    const { configuracao } = useConfiguracoesPedidoContext();
    const [produtosSelecionados, setProdutosSelecionados] = useState<IProdutoPedido[]>([]);
    const [loadingVincularEndereco, setLoadingVincularEndereco] = useState(false);
    const [loadingVincularCliente, setLoadingVincularCliente] = useState(false);
    const [drawerCuponsAberto, setDrawerCupons] = useState(false);
    const { aplicarCupom: aplicarCupomAPI, removerCupom: removerCupomAPI } = useCuponsPedido();

    const [switchNotaFiscal, setSwitchNotaFiscal] = useState(false);
    const handleSwitchNotaFiscal = () => {
        setSwitchNotaFiscal(!switchNotaFiscal);
    }

    const [imprimirOpen, setImprimirOpen] = useState<boolean>(false);
    const abrirModalImpressao = () => setImprimirOpen(true);
    const fecharModalImpressao = () => setImprimirOpen(false);

    const [notaFiscalOpen, setNotaFiscalOpen] = useState<boolean>(false);
    const abrirModalNotaFiscal = () => {
        setNotaFiscalOpen(true);
    }
    const fecharModalNotaFiscal = () => {
        setNotaFiscalOpen(false);
        fecharModalImpressao();
    };
    const handleModalNotaFiscal = () => {
        setNotaFiscalOpen(!notaFiscalOpen);
    }

    const [switchImprimirAutomatico, setSwitchImprimirAutomatico] = useState(true);
    const handleSwitchImprimirAutomatico = () => {
        setSwitchImprimirAutomatico(!switchImprimirAutomatico);
    }

    const [drawerPagamentoAberto, setDrawerPagamento] = useState(false);

    const abrirDrawerPagamento = () => {
        setDrawerPagamento(true);
    }

    const fecharDrawerPagamento = () => {
        setDrawerPagamento(false);
    }

    const [drawerDividirAberto, setDrawerDividir] = useState(false);

    const abrirDrawerDividir = () => {
        setDrawerDividir(true);
    }

    const fecharDrawerDividir = () => {
        setDrawerDividir(false);
    }

    const abrirDrawerCupons = () => {
        setDrawerCupons(true);
    };

    const fecharDrawerCupons = () => {
        setDrawerCupons(false);
    };

    const fluxoContexto = useMemo(() => {
        return configuracao?.fluxoOperacao?.fluxosContextos?.find(fluxo => fluxo.tipo === pedido?.tipo);
    }, [configuracao, pedido])

    const { ultimoPedidoAtualizado } = useWebSocketPedidosContext();

    useEffect(() => {
        const match = matchPath('/pedidos/operacao/edicao/:uuid', location.pathname);
        if (match && match.params.uuid) {
            handleBuscarPedido(match.params.uuid)
        }
    }, [location]);

    useEffect(() => {
        if (ultimoPedidoAtualizado && pedido) {
            if (!ultimoPedidoAtualizado.uuid || !pedido.uuid) return;
    
            if (ultimoPedidoAtualizado.uuid === pedido.uuid) {
                setTimeout(() => {
                    handleBuscarPedido(pedido.uuid);
                }, 100);
            }
        }
    }, [ultimoPedidoAtualizado]);
    

    const [searchParams] = useSearchParams();
    const redirect = searchParams.get("redirect");

    const handleBuscarPedido = async (pedidoUuid: string) => {
        try {
            const pedidoEncontrado = await buscarPedido(pedidoUuid);
    
            if (!pedidoEncontrado || !pedidoEncontrado.uuid) {
                console.warn("Pedido não encontrado:", pedidoEncontrado);
                return;
            }
    
            setPedido(pedidoEncontrado);
        } catch (erro) {
            console.error("Erro ao buscar pedido:", erro);
        }
    };
    

    const { estabelecimento } = useAutenticacaoContext();
    const { calcularFrete, loading: loadingEntregas } = useEntregas();

    const cancelarPedido = async () => {
        if (!pedido?.uuid) return;

        const { pedido: pedidoCancelado } = await atualizarStatus({
            pedidoUuid: pedido?.uuid ?? v4,
            status: StatusPedidoDescricao.Cancelado,
            pagamentos: pedido.pagamentos
        });
        if (pedido) {
            if (redirect) {
                navigate(redirect);
            } else {
                navigate("/pedidos/listagem");
            }
            snackbar("Pedido cancelado com sucesso", { severity: "success" })
        }
    }

    const configuracaoNFCe = useMemo(() => {
        return fluxoContexto?.configuracoesComandas.find(config => config.tipo == TipoAnexoPedido.nota_fiscal);
    }, [fluxoContexto])

    const valorRecebido = useMemo(() => {
        let total = 0;

        const pagamentosDinheiro = pedido?.pagamentos?.filter(pagamento => pagamento.metodoPagamento == MetodoPagamento.Dinheiro) ?? [];
        pagamentosDinheiro?.map(pagamento => {
            total = total + (pagamento.valorRecebido ?? 0);
        })

        return total;
    }, [pedido]);
    const valorTroco = useMemo(() => valorRecebido ? (valorRecebido - (pedido?.valorTotal ?? 0)) : 0, [pedido, valorRecebido])

    const editarProduto = async (produto: IProdutoPedido) => {
        await removerProduto(produto.uuid);
        await adicionarProduto(produto);
    }

    const alterarPagamentos = async (pagamentos?: IPagamentoPedido[]) => {
        if (!pagamentos) {
            pagamentos = [];
        }

        if (pedido) {
            const resultado = await atualizarPagamentosPedido(
                pedido.uuid,
                pagamentos
            );

            if (!resultado) {
                return snackbar("Ocorreu um erro ao atualizar o método de pagamento", { severity: "error" });
            }

            handleBuscarPedido(pedido.uuid);
        }
    }

    const alterarTipo = async (tipo?: TipoPedido) => {
        if (!tipo) {
            return snackbar("Tipo inválido", { severity: 'error' });
        }

        if (pedido) {
            const resultado = await alterarTipoPedido(
                pedido.uuid,
                tipo
            );

            if (!resultado) {
                return snackbar("Ocorreu um erro ao alterar o tipo do pedido", { severity: "error" });
            }

            handleBuscarPedido(pedido.uuid);
        }
    }

    const alterarTaxaServico = async (percentual?: number) => {
        if (!pedido) {
            return snackbar("Pedido não encontrado", { severity: "error" });
        }

        const resultado = await alterarTaxaServicoPedido(pedido.uuid, percentual);

        if (!resultado) {
            return snackbar("Ocorreu um erro ao atualizar a taxa de serviço", { severity: "error" });
        }

        await handleBuscarPedido(pedido.uuid);
    }

    const removerCuponsProdutosRemovidos = async (produtosRemovidos: IProdutoPedido[]) => {
        if (!pedido?.cupons || pedido.cupons.length === 0 || !produtosRemovidos || produtosRemovidos.length === 0) {
            return;
        }

        // Filtrar apenas cupons associados aos produtos removidos
        const cuponsParaRemover = filtrarCuponsProdutosRemovidos(pedido.cupons, produtosRemovidos);
        
        if (cuponsParaRemover.length === 0) {
            return;
        }

        // Remover apenas os cupons associados aos produtos removidos
        for (const cupom of cuponsParaRemover) {
            if (cupom.codigo && cupom.tipo) {
                await removerCupomAPI(pedido.uuid, cupom.codigo, cupom.tipo as any);
            }
        }
        
        // Recarregar o pedido para atualizar os valores
        await handleBuscarPedido(pedido.uuid);
        
        if (cuponsParaRemover.length === 1) {
            snackbar("Cupom removido devido à remoção do produto", { severity: "info" });
        } else {
            snackbar(`${cuponsParaRemover.length} cupons removidos devido à remoção do(s) produto(s)`, { severity: "info" });
        }
    };

    const adicionarProdutosCombo = async (novosProdutos: IProdutoPedido[]): Promise<void> => {
        if (pedido?.pagamentos?.length) {
            await alterarPagamentos([]);
        }
        if (!pedido) return;
        for (const produto of novosProdutos) {
            await adicionarProdutoPedido(produto, pedido.uuid);
        }
        const pedidoAtualizado = await buscarPedido(pedido.uuid);
        if (pedidoAtualizado) {
            setPedido(pedidoAtualizado);
        }
    };

    const adicionarProduto = async (produto: IProdutoPedido): Promise<IProdutoPedido | undefined> => {
        if (pedido?.pagamentos?.length) {
            await alterarPagamentos([]);
            snackbar("O método de pagamento foi removido", { severity: "info" })
        }

        // Não precisa remover cupons ao adicionar produto, apenas reaplicar se necessário
        // Os cupons serão reaplicados automaticamente quando o pedido for atualizado

        const produtoJaExiste = pedido?.produtos?.find(produtoCarrinho =>
            produtoCarrinho.uuidProdutoReferencia === produto.uuidProdutoReferencia &&
            !produtoCarrinho.comboUuid
        );

        if (
            produtoJaExiste &&
            produtoJaExiste.uuid &&
            produto.tipo == TipoProduto.industrializado
        ) {
            if (!pedido) {
                return undefined;
            }
            await aumentarQuantidade(produtoJaExiste.uuid);
            // Buscar o produto atualizado após aumentar quantidade
            const pedidoAtualizado = await buscarPedido(pedido.uuid);
            if (pedidoAtualizado) {
                setPedido(pedidoAtualizado);
                const produtoAtualizado = pedidoAtualizado.produtos?.find(
                    (p: IProdutoPedido) => p.uuid === produtoJaExiste.uuid
                );
                return produtoAtualizado || produtoJaExiste;
            }
            return produtoJaExiste;
        }

        if (
            produtoJaExiste &&
            produtoJaExiste.uuid &&
            produto.tipo == TipoProduto.preparado &&
            (
                produto.configuracao?.habilitarComplementos == false ||
                produto.configuracao?.habilitarObservacao == false
            )
        ) {
            if (!pedido) {
                return undefined;
            }
            await aumentarQuantidade(produtoJaExiste.uuid);
            // Buscar o produto atualizado após aumentar quantidade
            const pedidoAtualizado = await buscarPedido(pedido.uuid);
            if (pedidoAtualizado) {
                setPedido(pedidoAtualizado);
                const produtoAtualizado = pedidoAtualizado.produtos?.find(
                    (p: IProdutoPedido) => p.uuid === produtoJaExiste.uuid
                );
                return produtoAtualizado || produtoJaExiste;
            }
            return produtoJaExiste;
        }

        const produtoTratado = tratarProduto(produto);
        if (!produtoTratado) {
            snackbar("Produto invalido", { severity: "error" })
            return undefined;
        }

        if (pedido) {
            await adicionarProdutoPedido(produtoTratado, pedido.uuid);
            // Aguardar o pedido ser atualizado para garantir que o produto foi adicionado
            const pedidoAtualizado = await buscarPedido(pedido.uuid);
            if (pedidoAtualizado) {
                setPedido(pedidoAtualizado);
                // Buscar o produto atualizado do pedido para retornar com o UUID correto
                // O produto mais recente adicionado será o último na lista ou o que corresponde ao uuidProdutoReferencia
                const produtoAdicionado = pedidoAtualizado.produtos?.find(
                    (p: IProdutoPedido) => p.uuidProdutoReferencia === produtoTratado.uuidProdutoReferencia
                );
                // Se encontrou o produto, retornar ele (com UUID do backend)
                // Caso contrário, retornar o produto tratado
                return produtoAdicionado || produtoTratado;
            }
            return produtoTratado;
        }
        return undefined;
    }

    const dividirPedido = async (produtosDivisao: string[]) => {
        if(!pedido) return;
        const resultado = await dividirPedidoHook(pedido?.uuid, produtosDivisao);
        handleBuscarPedido(pedido?.uuid);
        return resultado;
    }

    const fecharPedido = async (dadosEmitirNota?: IDadosEmitirNotaFiscal, pagamentosParametro?: IPagamentoPedido[]) => {
        if (switchNotaFiscal && !notaFiscalOpen) {
            return handleModalNotaFiscal();
        }

        if (pedido) {
            let erroTroco = false;
            if (pagamentosParametro?.length) {
                pagamentosParametro.map(pagamento => {
                    if (pagamento.metodoPagamento == MetodoPagamento.Dinheiro && valorTroco < 0) {
                        erroTroco = true;
                    }
                })
            } else {
                pedido?.pagamentos?.map(pagamento => {
                    if (pagamento.metodoPagamento == MetodoPagamento.Dinheiro && valorTroco < 0) {
                        erroTroco = true;
                    }
                })
            }

            if (erroTroco) {
                return snackbar("Troco inválido", { severity: "error" });
            }

            const { pedido: pedidoAtualizado } = await atualizarStatus({
                pedidoUuid: pedido.uuid,
                status: StatusPedidoDescricao.Concluído,
                pagamentos: pagamentosParametro ?? pedido?.pagamentos
            });

            if (switchImprimirAutomatico) {
                gerarAnexosBotaoFecharPedido(pedidoAtualizado, dadosEmitirNota);
            }

            if (pedidoAtualizado) {
                if (redirect) {
                    navigate(redirect);
                } else {
                    navigate("/pedidos/listagem");
                }
            }
        }
    }

    const gerarAnexosBotaoFecharPedido = async (pedido: IPedido, dados?: IDadosEmitirNotaFiscal) => {
        if (switchNotaFiscal == true) {
            setTimeout(async () => await emitirNotaFiscalConsumidor(pedido.uuid, dados), 1000);
            handleModalNotaFiscal();
        }

        const configuracoesComanda = fluxoContexto?.configuracoesComandas.filter(config => {
            if (config.gatilhos.includes(TipoGatilhoComanda.BOTAO_CONCLUIR) && config.obrigatorio && config.imprimirAutomaticamente !== false) {
                return config;
            }
        });

        const configuracoesNaoFiscais = configuracoesComanda?.filter(config => config.tipo != TipoAnexoPedido.nota_fiscal);
        configuracoesNaoFiscais?.forEach((config, index) => {
            setTimeout(async () => {
                await gerarAnexoPedido(pedido.uuid, config.tipo, config.uuid);
            }, 1000 * (index + 1));
        });
    }

    const aumentarQuantidade = async (uuid: string) => {
        if (pedido?.pagamentos?.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" })
        }

        // Não precisa remover cupons ao aumentar quantidade, apenas reaplicar se necessário

        if (!pedido?.produtos || !pedido?.produtos.length) {
            return snackbar("Nenhum produto no carrinho", { severity: "error" });
        }

        const index = pedido?.produtos.findIndex(produto => produto.uuid === uuid);

        if (index === -1) {
            return snackbar("Produto não encontrado", { severity: "error" });
        }

        if (pedido) {
            await adicionarProdutoPedido(pedido?.produtos[index], pedido.uuid);
            await handleBuscarPedido(pedido.uuid);
        }
    };

    const diminuirQuantidade = async (uuid: string) => {
        if (pedido?.pagamentos?.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" })
        }

        if (!pedido?.produtos || !pedido?.produtos.length) {
            return snackbar("Nenhum produto no carrinho", { severity: "error" });
        }

        const index = pedido?.produtos.findIndex(produto => produto.uuid === uuid);

        if (index === -1) {
            return snackbar("Produto não encontrado", { severity: "error" });
        }

        const produtoAtual = pedido.produtos[index];

        if (!produtoAtual.quantidade || produtoAtual.quantidade <= 1) {
            // Se a quantidade chegou a zero ou vai chegar, o produto será removido
            await removerProduto(uuid)
        } else {
            if (pedido) {
                await removerProdutoPedido(produtoAtual.uuid, pedido.uuid)
                await handleBuscarPedido(pedido.uuid);
            }
        }
    };

    const removerProduto = async (uuid: string) => {
        if (pedido?.pagamentos?.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" })
        }

        if (!pedido?.produtos?.length) {
            return;
        }

        const index = pedido?.produtos?.findIndex(produto => produto.uuid === uuid);

        if (index === -1 || index === undefined) {
            return snackbar("Não foi possível remover o produto", { severity: "error" });
        }

        const produtoRemovido = pedido.produtos[index];
        const quantidade = !produtoRemovido.quantidade ? 1 : produtoRemovido.quantidade;

        // Verificar e remover apenas cupons associados ao produto removido
        if (pedido.cupons && pedido.cupons.length > 0) {
            await removerCuponsProdutosRemovidos([produtoRemovido]);
        }

        if (pedido) {
            await removerProdutoPedido(
                produtoRemovido.uuid,
                pedido.uuid,
                quantidade
            )
            await handleBuscarPedido(pedido.uuid);
        }
    }

    const selecionarProduto = (produto: IProdutoPedido) => {
        const produtoIndex = produtosSelecionados.findIndex(produtoProcurado => produtoProcurado.uuid == produto.uuid);

        if (produtoIndex !== -1) {
            const novosProdutos = produtosSelecionados;
            novosProdutos.splice(produtoIndex, 1);
            setProdutosSelecionados([...novosProdutos]);
            return;
        }

        setProdutosSelecionados([...produtosSelecionados, produto]);
    }

    const numeroTotalItens = useMemo(() => {
        let total = 0;

        pedido?.produtos?.map(produto => {
            total += produto.quantidade ?? 1
        })

        return total
    }, [pedido])

    const gerarAnexosModal = async (configuracaoComanda: string) => {
        if (loading) {
            return;
        }

        const configuracaoComandaEncontrada = fluxoContexto
            ?.configuracoesComandas
            ?.find(config => config.uuid == configuracaoComanda);

        if (!configuracaoComandaEncontrada || !pedido) {
            return snackbar("Nenhuma configuração de comanda selecionada", { severity: "error" });
        }

        if (configuracaoComandaEncontrada.tipo == TipoAnexoPedido.nota_fiscal) {
            return abrirModalNotaFiscal();
        }

        await gerarAnexoPedido(pedido.uuid, configuracaoComandaEncontrada.tipo, configuracaoComandaEncontrada.uuid, pedido.produtos);
        snackbar("Comanda enviada para fila de impressão", { severity: "success" });
        fecharModalImpressao();
    }


    const gerarAnexosBotaoImprimirComandas = async () => {
        if (loading) {
            return;
        }

        if (!pedido?.produtos || !pedido?.produtos.length) {
            return snackbar("Nenhum produto no carrinho", { severity: "error" });
        }

        const configuracoesComanda = fluxoContexto?.configuracoesComandas.filter(config => {
            if (config.gatilhos.includes(TipoGatilhoComanda.BOTAO_IMPRIMIR)) {
                return config;
            }
        });

        const configuracoesNaoFiscais = configuracoesComanda?.filter(config => config.tipo != TipoAnexoPedido.nota_fiscal);
        configuracoesNaoFiscais?.forEach((config, index) => {
            setTimeout(async () => {
                await gerarAnexoPedido(pedido.uuid, config.tipo, config.uuid, produtosSelecionados);
            }, 1000 * (index + 1));
        });
        setProdutosSelecionados([]);
    }

    const vincularEndereco = async (props?: IEndereco) => {
        let regiao = props?.regiao;

        if (props && configuracao?.tipoCobrancaEntrega == TipoCobrancaEntrega.DISTANCIA) {
            if (!configuracao?.distanciasEntrega?.length) {
                return snackbar("Nenhuma distância configurada para calcular o frete", { severity: 'error' });
            }

            if (!estabelecimento?.endereco) {
                return snackbar("O endereço do estabelecimento não está configurado", { severity: 'error' });
            }

            setLoadingVincularEndereco(true);

            const regiaoGerada = await calcularFrete({
                enderecoDestino: props,
                distancias: configuracao?.distanciasEntrega,
                enderecoOrigem: estabelecimento?.endereco
            });

            setLoadingVincularEndereco(false);

            if (!regiaoGerada) {
                return;
            }

            regiao = regiaoGerada;
        }

        if (props && configuracao?.tipoCobrancaEntrega == TipoCobrancaEntrega.REGIAO) {
            if (!regiao) {
                return snackbar("Região não configurada", { severity: "error" });
            }
        }

        const endereco = {
            ...props,
            regiao
        }

        if (pedido) {
            // Reaplicar cupons quando endereço mudar (não remover, apenas reaplicar)
            // Os cupons serão reaplicados automaticamente quando o pedido for atualizado

            const enderecoAlterado = await atualizarEndereco(pedido.uuid, props ? endereco : undefined);
            if (!enderecoAlterado) {
                return snackbar("Ocorreu um erro ao alterar o endereço", { severity: "error" })
            }

            handleBuscarPedido(pedido.uuid);
            if (props) {
                snackbar("Endereço alterado com sucesso", { severity: "success" });
            }
        }
    }

    const alterarValorFrete = async (valor: number) => {
        if (!pedido) {
            return snackbar("Pedido não encontrado", { severity: "error" });
        }

        // Reaplicar cupons quando valor do frete mudar (não remover, apenas reaplicar)
        // Os cupons serão reaplicados automaticamente quando o pedido for atualizado

        if (!pedido.endereco) {
            return snackbar("Endereço indefinido", { severity: "error" });
        }

        const regiaoAtualizada: IRegiao = {
            preco: valor,
            descricao: pedido.endereco.regiao?.descricao ?? "",
            uuid: pedido.endereco.regiao?.uuid,
            configuracaoUuid: pedido.endereco.regiao?.configuracaoUuid,
            cadastrar: pedido.endereco.regiao?.cadastrar,
            cadastradaAs: pedido.endereco.regiao?.cadastradaAs,
        };

        const enderecoAtualizado: IEndereco = {
            ...pedido.endereco,
            regiao: regiaoAtualizada
        };

        const sucesso = await atualizarEndereco(pedido.uuid, enderecoAtualizado);
        if (!sucesso) {
            return snackbar("Ocorreu um erro ao atualizar o frete", { severity: "error" });
        }

        await handleBuscarPedido(pedido.uuid);
        snackbar("Frete atualizado com sucesso", { severity: "success" });
    }

    const emitirNotaFiscal = async (dados: IDadosEmitirNotaFiscal) => {
        if (loading) {
            return;
        }

        if (!pedido) {
            return;
        }

        await emitirNotaFiscalConsumidor(pedido.uuid, dados);
        snackbar("Nota fiscal enviada para fila de impressão", { severity: "success" });
        fecharModalNotaFiscal();

        if(switchNotaFiscal) {
            fecharPedido();
            handleSwitchNotaFiscal();
        }
    }

    const vincularClientePedido = async (cliente: ICliente) => {
        if (!pedido) {
            return snackbar("Pedido não encontrado", { severity: "error" });
        }

        if (pedido.tipo !== TipoPedido.mesa) {
            return snackbar("A vinculação de cliente é permitida apenas para pedidos do tipo mesa", { severity: "error" });
        }

        if (!cliente.uuid) {
            return snackbar("Cliente inválido", { severity: "error" });
        }

        setLoadingVincularCliente(true);
        try {
            const sucesso = await vincularClienteHook(pedido.uuid, cliente);
            if (!sucesso) {
                return snackbar("Ocorreu um erro ao vincular o cliente", { severity: "error" });
            }

            await handleBuscarPedido(pedido.uuid);
            snackbar("Cliente vinculado com sucesso", { severity: "success" });
        } catch (error) {
            snackbar("Ocorreu um erro ao vincular o cliente", { severity: "error" });
        } finally {
            setLoadingVincularCliente(false);
        }
    }

    const desvincularClientePedido = async () => {
        if (!pedido) {
            return snackbar("Pedido não encontrado", { severity: "error" });
        }

        if (pedido.tipo !== TipoPedido.mesa) {
            return snackbar("A desvinculação de cliente é permitida apenas para pedidos do tipo mesa", { severity: "error" });
        }

        setLoadingVincularCliente(true);
        try {
            const sucesso = await desvincularClienteHook(pedido.uuid);
            if (!sucesso) {
                return snackbar("Ocorreu um erro ao desvincular o cliente", { severity: "error" });
            }

            await handleBuscarPedido(pedido.uuid);
            snackbar("Cliente desvinculado com sucesso", { severity: "success" });
        } catch (error) {
            snackbar("Ocorreu um erro ao desvincular o cliente", { severity: "error" });
        } finally {
            setLoadingVincularCliente(false);
        }
    }

    const aplicarCupom = async (cupom: ICupom) => {
        if (!pedido) {
            return snackbar("Pedido não encontrado", { severity: "error" });
        }

        // Verificar se o cupom já foi aplicado
        // Para cupons de fidelidade, verificar por programaFidelidadeUuid e nome
        // Para outros cupons, verificar por código e tipo
        const jaExiste = pedido.cupons?.some((c) => {
            // Se é um cupom de fidelidade (tem programaFidelidadeUuid)
            if (cupom.programaFidelidadeUuid && c.programaFidelidadeUuid) {
                // Verificar se é do mesmo programa
                if (c.programaFidelidadeUuid === cupom.programaFidelidadeUuid) {
                    // Se é o cupom principal (nome "Itens Fidelidade" ou "Cashback Fidelidade")
                    if (cupom.nome === "Itens Fidelidade" || cupom.nome === "Cashback Fidelidade") {
                        // Verificar se já existe um cupom principal do mesmo programa
                        return c.nome === cupom.nome;
                    }
                    // Para cupons de itens individuais, verificar se já existe um com o mesmo UUID do produto no código
                    if (cupom.codigo && c.codigo && cupom.codigo.includes('-') && c.codigo.includes('-')) {
                        // Extrair o UUID do produto do código (última parte após o último hífen)
                        const partesCupom = cupom.codigo.split('-');
                        const partesExistente = c.codigo.split('-');
                        // Se ambos têm o mesmo UUID do produto (última parte)
                        if (partesCupom.length > 0 && partesExistente.length > 0) {
                            const uuidProdutoCupom = partesCupom[partesCupom.length - 1];
                            const uuidProdutoExistente = partesExistente[partesExistente.length - 1];
                            if (uuidProdutoCupom === uuidProdutoExistente && uuidProdutoCupom.length > 10) {
                                return true;
                            }
                        }
                    }
                }
            }
            // Verificação padrão por código e tipo
            return c.codigo === cupom.codigo && c.tipo === cupom.tipo;
        });

        if (jaExiste) {
            snackbar("Este cupom já foi aplicado", { severity: "warning" });
            return;
        }

        // Chamar API para aplicar cupom
        const sucesso = await aplicarCupomAPI(pedido.uuid, cupom);
        if (sucesso) {
            await handleBuscarPedido(pedido.uuid);
        }
    };

    const removerCupom = async (codigo: string, tipo: string) => {
        if (!pedido) {
            return snackbar("Pedido não encontrado", { severity: "error" });
        }

        // Chamar API para remover cupom
        const sucesso = await removerCupomAPI(pedido.uuid, codigo, tipo as any);
        if (sucesso) {
            await handleBuscarPedido(pedido.uuid);
        }
    };

    // Implementação dos atalhos F8 e F9
    useHotkeys(['f8'], () => {
        window.dispatchEvent(new CustomEvent('limparFiltroProdutos'));
        window.dispatchEvent(new CustomEvent('resetarModoTeclado'));
        fecharPedido();
    }, {
        enabled: !isModoTecladoAtivo() // Desabilitar quando modo teclado estiver ativo
    }, [
        pedido,
        switchNotaFiscal,
        notaFiscalOpen,
        switchImprimirAutomatico,
        fluxoContexto
    ]);

    useHotkeys(['f9'], () => {
        window.dispatchEvent(new CustomEvent('limparFiltroProdutos'));
        window.dispatchEvent(new CustomEvent('resetarModoTeclado'));
        if (pedido) {
            handleBuscarPedido(pedido.uuid);
            snackbar("Pedido atualizado", { severity: "success" });
        }
    }, {
        enabled: !isModoTecladoAtivo() // Desabilitar quando modo teclado estiver ativo
    }, [
        pedido
    ]);


    return (
        <OperacaoEdicaoContext.Provider value={{
            adicionarProduto,
            adicionarProdutosCombo,
            configuracao,
            alterarPagamentos,
            aumentarQuantidade,
            diminuirQuantidade,
            removerProduto,
            loading,
            loadingAnexo,
            fecharPedido,
            cancelarPedido,
            selecionarProduto,
            produtosSelecionados,
            fluxoContexto,
            pedido,
            recarregarPedido: async () => { if (pedido?.uuid) await handleBuscarPedido(pedido.uuid); },
            editarProduto,
            numeroTotalItens,
            gerarAnexosBotaoImprimirComandas,
            loadingVincularEndereco,
            vincularEndereco,
            alterarTipo,
            gerarAnexosModal,
            emitirNotaFiscal,
            alterarTaxaServico,
            abrirModalImpressao,
            abrirDrawerPagamento,
            fecharDrawerPagamento,
            drawerPagamentoAberto,
            abrirDrawerDividir,
            drawerDividirAberto,
            fecharDrawerDividir,
            dividirPedido,
            switchImprimirAutomatico,
            handleSwitchImprimirAutomatico,
            configuracaoNFCe,
            handleSwitchNotaFiscal,
            switchNotaFiscal,
            alterarValorFrete,
            vincularClientePedido,
            desvincularClientePedido,
            loadingVincularCliente,
            cupons: pedido?.cupons || [],
            aplicarCupom,
            removerCupom,
            drawerCuponsAberto,
            abrirDrawerCupons,
            fecharDrawerCupons
        }}>
            {fluxoContexto && (
                <ImprimirModal
                    fluxoContexto={fluxoContexto}
                    fechar={fecharModalImpressao}
                    imprimir={gerarAnexosModal}
                    loading={loadingAnexo}
                    open={imprimirOpen}
                />
            )}
            <EmitirNotaFiscalModal
                cancelar={fecharModalNotaFiscal}
                concluir={emitirNotaFiscal}
                open={notaFiscalOpen}
                loading={loadingAnexo}
            />
            {pedido && (
                <>
                    <DividirPagamento
                        aberto={drawerPagamentoAberto}
                        fechar={fecharDrawerPagamento}
                        valorTotalPedido={pedido?.valorTotal ?? 0}
                        produtos={pedido?.produtos ?? []}
                        fecharPedido={fecharPedido}
                        tipoPedido={pedido.tipo as TipoPedido}
                        valorFrete={pedido.valorFrete ?? 0}
                    />
                    <DividirPedido 
                        aberto={drawerDividirAberto}
                        fechar={fecharDrawerDividir}
                        valorTotalPedido={pedido.valorProdutos}
                        produtos={pedido.produtos ?? []}
                        dividir={dividirPedido}
                    />
                </>
            )}
            {children}
        </OperacaoEdicaoContext.Provider>
    );
} 